/**
 * :func:`PanelVisibilityProvider` — publishes the owning dockview panel's
 * visibility to its subtree (REQ-STATUS-099).
 *
 * The component half; the context and the access hook live in the ``.ts``
 * sibling (:file:`./panel-visibility-context`), which is where the rationale
 * is written. Same split as :file:`@/system-status/system-status-stream-provider`.
 */
import type { ReactNode } from "react";
import { PanelVisibilityContext } from "@/multi-pane/panel-visibility-context";
import { usePanelVisible } from "@/multi-pane/use-panel-visible";
import type { PanelVisibilityApi } from "@/selection/use-keep-selected-row-in-view";

/** Props for :func:`PanelVisibilityProvider`. */
export interface PanelVisibilityProviderProps {
  /** The owning panel's dockview api; omit outside a dockview host. */
  panelApi?: PanelVisibilityApi;
  children: ReactNode;
}

/**
 * Publish the owning dockview panel's visibility to everything beneath it.
 *
 * Mount this in a panel's dockview wrapper, around the panel body.
 */
export function PanelVisibilityProvider({
  panelApi,
  children,
}: PanelVisibilityProviderProps): React.ReactElement {
  const visible = usePanelVisible(panelApi);
  return (
    <PanelVisibilityContext.Provider value={visible}>{children}</PanelVisibilityContext.Provider>
  );
}
